import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import './BandejaEntrada.css';
import UploadZone from '../ZonaDeSubida/ZonaDeSubida';
import FilePreviewModal from '../FilePreviewModal/FilePreviewModal';

const getFileIcon = (filename) => {
  if (!filename) return 'bi bi-file-earmark';
  const ext = filename.split('.').pop()?.toLowerCase();
  const map = { pdf: 'bi-filetype-pdf', doc: 'bi-filetype-docx', docx: 'bi-filetype-docx', xls: 'bi-filetype-xlsx', xlsx: 'bi-filetype-xlsx', csv: 'bi-filetype-xlsx', png: 'bi-filetype-jpg', jpg: 'bi-filetype-jpg', jpeg: 'bi-filetype-jpg', gif: 'bi-filetype-jpg', zip: 'bi-file-zip', rar: 'bi-file-zip' };
  return 'bi ' + (map[ext] || 'bi-file-earmark');
};

const getFileColor = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  const map = { pdf: '#e53e3e', doc: '#2b6cb0', docx: '#2b6cb0', xls: '#38a169', xlsx: '#38a169', csv: '#38a169', png: '#805ad5', jpg: '#805ad5', jpeg: '#805ad5', gif: '#805ad5', zip: '#d69e2e', rar: '#d69e2e' };
  return map[ext] || '#718096';
};

const BandejaEntrada = ({ entidadId, targetEntidadId, isAdmin, addToast }) => {
  const [folderId, setFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [preview, setPreview] = useState({ open: false, url: '', file: null });
  const [subiendo, setSubiendo] = useState(false);

  const resolvedEntidadId = entidadId || targetEntidadId || null;

  const obtenerBandeja = useCallback(async () => {
    if (!resolvedEntidadId) return;
    setLoading(true);
    try {
      const res = await api.post('/obtenerBandejaEntrada', { entidadId: resolvedEntidadId });
      setFolderId(res.data.folderId);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al obtener la bandeja');
    } finally {
      setLoading(false);
    }
    }, [resolvedEntidadId]);

  useEffect(() => { obtenerBandeja(); }, [obtenerBandeja]);

  const cargarArchivos = async () => {
    if (!folderId) return;
    try {
      const res = await api.get(`/obtenerCarpetaPorId?carpetaId=${folderId}`);
      setArchivos(res.data.contenido_actual || []);
    } catch {
      setArchivos([]);
    }
  };

  useEffect(() => { cargarArchivos(); }, [folderId]);

  const handleSubir = async (e) => {
    const files = e.target ? e.target.files : e;
    if (!files || files.length === 0) return;

    const file = files[0];
    const nombres = archivos.filter(a => a.tipo === 'Archivo').map(a => a.nombre);
    let nombreFinal = file.name;
    if (nombres.includes(nombreFinal)) {
      const base = file.name.replace(/(\.[^.]+)$/, '');
      const ext = file.name.match(/(\.[^.]+)$/)?.[1] || '';
      let c = 1;
      while (nombres.includes(`${base} (${c})${ext}`)) c++;
      nombreFinal = `${base} (${c})${ext}`;
    }
    const renamedFile = nombreFinal !== file.name ? new File([file], nombreFinal, { type: file.type }) : file;

    setSubiendo(true);
    const formData = new FormData();
    formData.append('archivo', renamedFile);
    formData.append('entidadId', resolvedEntidadId);
    try {
      await api.post(`/subirArchivo?carpetaId=${folderId}`, formData);
      if (addToast) addToast('Archivo subido correctamente', 'success');
      cargarArchivos();
    } catch {
      if (addToast) addToast('Error al subir archivo', 'error');
    } finally {
      setSubiendo(false);
    }
  };

  const handleDescargar = async (archivo) => {
    try {
      const res = await api.get(`/descargarArchivo?archivoId=${archivo.id}`);
      const url = res.data.url || res.data.downloadUrl;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', archivo.nombre);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch { console.error('Error al descargar'); }
  };

  const handlePrevisualizar = async (archivo) => {
    try {
      setPreview({ open: true, url: '', file: archivo });
      const res = await api.get(`/previsualizarArchivo?archivoId=${archivo.id}`);
      if (res.data?.url) setPreview(prev => ({ ...prev, url: res.data.url }));
      else { setPreview({ open: false, url: '', file: null }); alert('No se pudo obtener vista previa'); }
    } catch { setPreview({ open: false, url: '', file: null }); alert('Error al previsualizar'); }
  };

  if (!resolvedEntidadId) return (
    <div className="dashboard-content">
      <div className="page-header"><h1 className="page-title">Bandeja de Entrada</h1></div>
      <div className="be-empty"><i className="bi bi-inbox"></i><p>Seleccioná una entidad para ver su bandeja de entrada.</p></div>
    </div>
  );

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title"><i className="bi bi-inbox" style={{ marginRight: 10 }}></i>Bandeja de Entrada</h1>
        <p className="page-subtitle">Subí tus documentos para que el estudio los reciba</p>
      </div>

      {loading ? (
        <div className="be-loading"><div className="be-spinner"></div><p>Preparando bandeja...</p></div>
      ) : error ? (
        <div className="be-error"><i className="bi bi-exclamation-triangle"></i> {error}</div>
      ) : (
        <>
          <UploadZone onSubir={handleSubir} loading={subiendo} />
          <div className="be-files">
            <h3><i className="bi bi-files"></i> Documentos</h3>
            {archivos.length === 0 ? (
              <div className="be-empty"><i className="bi bi-inbox"></i><p>No hay documentos aún. Subí uno arriba.</p></div>
            ) : (
              <ul className="be-list">
                {archivos.map(item => (
                  <li key={item.id} className="be-item">
                    <div className="be-item-left" onClick={() => item.tipo === 'Archivo' ? handlePrevisualizar(item) : null}>
                      <i className={getFileIcon(item.nombre)} style={{ color: getFileColor(item.nombre), fontSize: '22px' }}></i>
                      <div>
                        <div className="be-item-name">{item.nombre}</div>
                        <div className="be-item-meta">{item.size || ''}{item.fecha ? ` - ${item.fecha}` : ''}</div>
                      </div>
                    </div>
                    <div className="be-item-actions" onClick={e => e.stopPropagation()}>
                      <button className="btn-accion" title="Descargar" onClick={() => handleDescargar(item)}><i className="bi bi-download"></i></button>
                      <button className="btn-accion" title="Vista previa" onClick={() => handlePrevisualizar(item)}><i className="bi bi-eye"></i></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <FilePreviewModal
        isOpen={preview.open}
        onClose={() => setPreview({ open: false, url: '', file: null })}
        fileUrl={preview.url}
        fileName={preview.file?.nombre || ''}
        onDownload={preview.file ? () => handleDescargar(preview.file) : null}
      />
    </div>
  );
};

export default BandejaEntrada;
